/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib util — WCAG relative-luminance and contrast-ratio maths, used
 *              to decide whether a tenant-chosen brand colour is legible on a
 *              given page ground before it is allowed to override a theme token
 *   extends:   -
 *   template:  none
 *   args:      -
 *   uses:      -
 *   siblings:  ./charts (brightnessByColor — YIQ perceived brightness, a
 *              different metric: it answers "is this fill dark?", not "is this
 *              ink readable on that ground?")
 *   tests:     -
 *
 * @module beebee-base/lib/utility/color-contrast
 */

/** WCAG 2.1 AA floor for large text and non-text UI components. */
const MIN_CONTRAST_LARGE_TEXT = 3;

/**
 * Parse a CSS colour into [r, g, b] (0-255).
 * Accepts `#rgb`, `#rrggbb` and `rgb()/rgba()`. Anything else returns null —
 * tenant colours come from a colour picker, so they are always hex in practice,
 * and guessing at a named colour would be worse than declining to judge.
 *
 * @param {string} color
 * @returns {number[]|null}
 */
function parseColor(color) {
  if (!color) {
    return null;
  }
  const value = String(color).trim();
  if (value.charAt(0) === '#') {
    let hex = value.slice(1);
    if (hex.length === 3) {
      // Shorthand: each nibble doubles (f -> ff), otherwise #fff reads near-black.
      hex = hex.split('').map(digit => digit + digit).join('');
    }
    if (hex.length !== 6 || !/^[0-9a-f]{6}$/i.test(hex)) {
      return null;
    }
    return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
  }
  if (value.indexOf('rgb') === 0) {
    const parts = value.match(/\d+(\.\d+)?/g);
    if (!parts || parts.length < 3) {
      return null;
    }
    return parts.slice(0, 3).map(part => Math.min(255, Math.round(Number(part))));
  }
  return null;
}

/**
 * WCAG 2.1 relative luminance (0 = black, 1 = white).
 *
 * @param {string} color
 * @returns {number|null} null when the colour cannot be parsed
 */
function relativeLuminance(color) {
  const rgb = parseColor(color);
  if (!rgb) {
    return null;
  }
  const [r, g, b] = rgb.map(channel => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * WCAG 2.1 contrast ratio between two colours (1 = identical, 21 = black/white).
 *
 * @param {string} colorA
 * @param {string} colorB
 * @returns {number|null} null when either colour cannot be parsed
 */
function contrastRatio(colorA, colorB) {
  const a = relativeLuminance(colorA);
  const b = relativeLuminance(colorB);
  if (a === null || b === null) {
    return null;
  }
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/**
 * Is `color` legible against every one of `grounds`?
 *
 * Every ground has to pass, not just the best one: a page ships more than one
 * surface (body and card differ), and a colour that only works on the friendlier
 * of the two is still unreadable half the time.
 *
 * An unparseable colour returns false — the caller's fallback is the theme
 * default, which is always safe, so declining to judge must not hand the tenant
 * colour a free pass.
 *
 * @param {string} color
 * @param {string[]} grounds
 * @param {number} [minRatio=MIN_CONTRAST_LARGE_TEXT]
 * @returns {boolean}
 */
function isReadableOn(color, grounds, minRatio = MIN_CONTRAST_LARGE_TEXT) {
  if (!color || !grounds || !grounds.length) {
    return false;
  }
  return grounds.every(ground => {
    const ratio = contrastRatio(color, ground);
    return ratio !== null && ratio >= minRatio;
  });
}

export { MIN_CONTRAST_LARGE_TEXT, contrastRatio, isReadableOn, parseColor, relativeLuminance };
//# sourceMappingURL=color-contrast.js.map
